<script lang="ts">
  import { onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { generateUUID } from '../lib/utils/uuid';
  import { showToast } from '../lib/toast';
  import type { TaskItem } from '../types';
  import { createEventDispatcher } from 'svelte';
  import { trackEvent } from '../lib/apiService';
  import { openDialogWithSlideUp, closeDialogWithSlideDown } from '../lib/utils/dialogAnimations';
  const dispatch = createEventDispatcher();

  export let taskList: TaskItem[] = [];
  export let availableTasks: TaskItem[] = [];

  let showTaskModal = false;
  let selectedTask: TaskItem | null = null;
  let originalTask: TaskItem | null = null;
  let is_new_task = false;
  let dialogElement: HTMLDialogElement;

  async function openTaskModal(task: TaskItem | null = null) {

    if (task === null) {
      selectedTask = {
        id: generateUUID(),
        user_name: '',
        task_id: '',
        label: '',
        icon: '',
      };
      is_new_task = true;
    } else {
      selectedTask = task;
      originalTask = JSON.parse(JSON.stringify(task));
    }

    // Save the initial task list to restore if canceled
    originalTask = JSON.parse(JSON.stringify(task));
    showTaskModal = true;

    // Wait for DOM update and trigger animation
    await new Promise(resolve => setTimeout(resolve, 0));
    if (dialogElement) {
      await openDialogWithSlideUp(dialogElement);
    }
  }

  async function closeTaskModal() {
    if (!selectedTask.task_id) {
      showToast($_('taskmanager.errors.noTaskSelected'), 'error');
      return;
    }
    if (!selectedTask.user_name) {
      showToast($_('taskmanager.errors.noUsername'), 'error');
      return;
    }
    if (is_new_task) {
      taskList.push(selectedTask);
      trackEvent('create_task', {
        module: "match_preparation",
        action_type: "create_data",
        description: 'Create task',
        value: selectedTask.task_id,
      });

    } else {
      taskList = taskList.map(t => t.id === selectedTask.id ? selectedTask : t);
      trackEvent('update_task', {
        module: "match_preparation",
        action_type: "update_data",
        description: 'Updates task',
        value: selectedTask.task_id,
      });
    }

    await closeDialogWithSlideDown(dialogElement);
    showTaskModal = false;
    dispatch('update', taskList);
  }

  async function cancelChanges() {
    await closeDialogWithSlideDown(dialogElement);
    showTaskModal = false;
  }
  
  async function removeTask(task: TaskItem) {
    if (!taskList) return;

    taskList = taskList.filter(t =>
      t.id !== task.id
    );

    await closeDialogWithSlideDown(dialogElement);
    showTaskModal = false;
    dispatch('update', taskList);

    trackEvent('delete_task', {
      module: "match_preparation",
      action_type: "delete_data",
      description: 'Delete task',
    });
  }

  function getTaskIcon(task_id: string): string {
    try {
      return availableTasks
        .filter(task => task.id === task_id)[0].icon;
    } catch (error) {
      console.error('Error getting task icon', error);
      return '';
    }
  }

  function getTaskLabel(task_id: string): string {
    return availableTasks
      .filter(task => task.id === task_id)[0].label;
  }

  function selectTask(task: TaskItem) {
    selectedTask.task_id = task.id;
    selectedTask.label = task.label;
    selectedTask.icon = task.icon; 
  }

</script>

<div class="mb-2">
  <!-- Show existing task assignments grouped by user -->
  {#key taskList}
    {#each taskList as task}
      <button class="secondary full-width align-left mb-2 flex justify-between" on:click={() => openTaskModal(task)}>
        <span>
          {getTaskIcon(task.task_id)}
          <span class="text text-sm mr-2">{getTaskLabel(task.task_id)} </span>
        </span>
        <span class="text text-sm">{task.user_name}</span>        
      </button>
    {/each}
  {/key}

</div>
  <!-- Add new task button -->
  <button class="outline full-width"
    on:click={() => openTaskModal()}
  >
    <ion-icon name="add-circle"></ion-icon>
    {$_('taskmanager.addTask')}
  </button>

<!-- Task Modal -->
{#if showTaskModal}
  <dialog bind:this={dialogElement} class="slide-up-dialog">
    <article class="mt-0">
      <header class="py-4">
        <h5>{$_('taskmanager.title')}</h5>
        <button class="close-button" on:click={cancelChanges}>×</button>
      </header>
              
      <div class=" grid grid-2 mb-1">
        {#each availableTasks as availableTask}
          <!-- Use isTaskSelected function to check if task is selected -->
          {@const isSelected = selectedTask.task_id === availableTask.id}
          <button
            class="small"
            class:contrast={isSelected}
            class:outline={!isSelected}
            class:secondary={!isSelected}
            on:click={() => selectTask(availableTask)}
          >
            {availableTask.icon} {availableTask.label}
          </button>
        {/each}
      </div>

      <label for="user_name" class="my-6">{$_('taskmanager.username')}</label>
      <input
        type="text"
        maxLength={35}
        bind:value={selectedTask.user_name}
        placeholder={$_('taskmanager.usernamePlaceholder')}
      />

      <footer class="flex-col">
        <button on:click={closeTaskModal} class="primary full-width" disabled={!selectedTask.user_name}>
          {$_('taskmanager.saveTask')}
        </button>
        {#if !is_new_task}
        <hr class="my-6"/>
        <div class="align-right full-width mt-6">
          <button type="button" class="secondary ghost small" on:click|preventDefault={() => removeTask(selectedTask)}>
          <ion-icon name="trash-outline"></ion-icon>
          {$_('common.delete')}
          </button>
          </div>
        {/if}
      </footer>
    </article>
  </dialog>
{/if}

<style>
</style> 